import React, { useEffect, useMemo, useState } from 'react';
import { Monitor, Play, Image as ImageIcon, Repeat } from 'lucide-react';
import { formatCountdown, formatMinutes, timeToMinutes } from './timeUtils';
import { resolveSlotAsset, resolveSlotLabel } from './labelResolver';
import { hashColor } from './color';

/**
 * LIVE-on card for the currently selected screen.
 *
 * Mental model:
 *   - Parent passes one `track` (the selected screen). We render a single
 *     focused card, not a strip — the marketing head wants to see exactly
 *     what's on the screen they clicked.
 *   - If the screen is looping ≥2 contents at this moment
 *     (track.concurrentSlots.length > 1), the card becomes a carousel that
 *     auto-advances through the looping contents. That matches how the TV
 *     resolver treats multi-content overlaps (see computeTimelineWindow).
 *   - Solo / off-air / offline fall back to single-card variants.
 *
 * The carousel is purely visual. The TV's actual rotation cadence is
 * controlled server-side; this card just rotates through the same set so
 * the viewer can confirm what's in the loop.
 */

const CAROUSEL_INTERVAL_MS = 4000;

const cardBase = {
  backgroundColor: 'var(--surface-raised, #fff)',
  border: '1px solid var(--border-default, #e7e5e4)',
  borderRadius: '12px',
  padding: '12px 14px',
  display: 'flex',
  gap: '12px',
  alignItems: 'center',
  minWidth: '260px',
  boxShadow: '0 1px 2px rgba(17, 24, 39, 0.04)',
};

const Thumbnail = ({ url, mediaType, accentColor }) => {
  const isVideo = mediaType === 'video' || (mediaType && String(mediaType).startsWith('video'));
  const box = {
    width: '52px',
    height: '52px',
    borderRadius: '8px',
    backgroundColor: accentColor || 'var(--gray-100, #f5f5f4)',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    position: 'relative',
  };
  if (!url) {
    return (
      <div style={box}>
        {isVideo ? <Play size={18} color="#fff" /> : <ImageIcon size={18} color="#fff" />}
      </div>
    );
  }
  return (
    <div style={box}>
      <img
        src={url}
        alt=""
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
      {isVideo && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          backgroundColor: 'rgba(0,0,0,0.18)',
        }}>
          <Play size={16} color="#fff" fill="#fff" />
        </div>
      )}
    </div>
  );
};

const LiveRibbon = ({ deviceName, isLooping, count }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: '6px',
    fontSize: '10px', fontWeight: 700, letterSpacing: '0.06em',
    textTransform: 'uppercase', color: 'var(--color-success-600, #059669)',
  }}>
    <span style={{
      width: '6px', height: '6px', borderRadius: '50%',
      backgroundColor: 'var(--color-success-500, #10b981)',
      display: 'inline-block',
      animation: 'timelineLivePulse 1.8s ease-in-out infinite',
    }} />
    <span>Live on {deviceName}</span>
    {isLooping && (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: '3px',
        marginLeft: '4px', padding: '1px 6px', borderRadius: '999px',
        backgroundColor: 'var(--color-success-50, #ecfdf5)',
        color: 'var(--color-success-700, #047857)',
        fontSize: '9px', letterSpacing: '0.04em',
      }}>
        <Repeat size={9} strokeWidth={2.5} />
        loop · {count}
      </span>
    )}
  </div>
);

const Dots = ({ count, active, onPick }) => (
  <div style={{ display: 'flex', gap: '5px', marginTop: '6px' }}>
    {Array.from({ length: count }).map((_, i) => (
      <button
        key={i}
        type="button"
        aria-label={`Show item ${i + 1}`}
        onClick={(e) => {
          e.stopPropagation();
          onPick(i);
        }}
        style={{
          width: '6px', height: '6px', borderRadius: '50%',
          border: 'none',
          padding: 0,
          backgroundColor: i === active
            ? 'var(--color-success-500, #10b981)'
            : 'var(--gray-300, #d4d4d4)',
          cursor: 'pointer',
        }}
      />
    ))}
  </div>
);

const OfflineCard = ({ deviceName, lastSeenLabel }) => (
  <div style={{ ...cardBase, opacity: 0.7 }}>
    <div style={{
      width: '52px', height: '52px', borderRadius: '8px',
      backgroundColor: 'var(--gray-100, #f5f5f4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
    }}>
      <Monitor size={22} color="var(--text-subtle)" />
    </div>
    <div style={{ minWidth: 0, flex: 1 }}>
      <div style={{
        fontSize: '13px', fontWeight: 700, color: 'var(--text-default)',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>{deviceName}</div>
      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
        {lastSeenLabel || 'Offline'}
      </div>
    </div>
  </div>
);

const OffAirCard = ({ deviceName, nextLabel, nextStart }) => (
  <div style={cardBase}>
    <div style={{
      width: '52px', height: '52px', borderRadius: '8px',
      backgroundColor: 'var(--gray-100, #f5f5f4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
    }}>
      <Monitor size={22} color="var(--color-success-500, #10b981)" />
    </div>
    <div style={{ minWidth: 0, flex: 1 }}>
      <div style={{
        fontSize: '13px', fontWeight: 700, color: 'var(--text-strong)',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>{deviceName}</div>
      <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
        {nextLabel
          ? `Off air — next at ${formatMinutes(nextStart)}`
          : 'Off air — nothing scheduled today'}
      </div>
      {nextLabel && (
        <div style={{ fontSize: '11px', color: 'var(--text-subtle)', marginTop: '2px' }}>
          {nextLabel}
        </div>
      )}
    </div>
  </div>
);

const OnAirNow = ({ track, contentMap, nowMs }) => {
  const concurrent = Array.isArray(track?.concurrentSlots) ? track.concurrentSlots : [];

  // The backend projects overlapping schedules into ONE slot with
  // loopEnabled=true and loopItems[] covering every content in the
  // overlap window. In that case concurrent.length is 1 but we still
  // need to carousel through loopItems so the card mirrors the TV.
  // Surface both cases as a unified `items` list of { contentId,
  // name?, mediaType?, endTime } entries.
  const items = useMemo(() => {
    if (concurrent.length === 0) return [];
    if (concurrent.length > 1) {
      return concurrent.map((s) => ({
        contentId: s.contentId,
        name: s.name,
        mediaType: s.mediaType,
        endTime: s.endTime,
        scheduleId: s.scheduleId,
        __slot: s,
      }));
    }
    const slot = concurrent[0];
    const li = Array.isArray(slot.loopItems) ? slot.loopItems : [];
    if (slot.loopEnabled && li.length > 1) {
      return li.map((it) => ({
        contentId: it.contentId || it.content_id,
        name: it.name,
        mediaType: it.mediaType || it.media_type,
        endTime: slot.endTime,
        scheduleId: slot.scheduleId,
        __slot: slot,
      }));
    }
    return [{
      contentId: slot.contentId,
      name: slot.name,
      mediaType: slot.mediaType,
      endTime: slot.endTime,
      scheduleId: slot.scheduleId,
      __slot: slot,
    }];
  }, [concurrent]);

  const isLooping = items.length > 1;
  const [index, setIndex] = useState(0);

  const loopKey = useMemo(
    () => `${track?.screenId || ''}:${items.map((x) => x.contentId || x.scheduleId).join('|')}`,
    [track?.screenId, items],
  );
  useEffect(() => { setIndex(0); }, [loopKey]);

  useEffect(() => {
    if (!isLooping) return undefined;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % items.length);
    }, CAROUSEL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isLooping, items.length, loopKey]);

  if (!track) return null;

  const isOnline = track.device?.status?.toLowerCase?.() === 'online';
  if (!isOnline) {
    let lastSeenLabel = 'Offline';
    if (track.lastSeenAt) {
      const d = new Date(track.lastSeenAt);
      if (!Number.isNaN(d.getTime())) {
        const hh = d.getHours();
        const mm = String(d.getMinutes()).padStart(2, '0');
        const ampm = hh < 12 ? 'AM' : 'PM';
        const h12 = hh % 12 || 12;
        lastSeenLabel = `offline since ${h12}:${mm} ${ampm}`;
      }
    }
    return (
      <div style={{ marginBottom: '14px' }}>
        <OfflineCard deviceName={track.deviceName} lastSeenLabel={lastSeenLabel} />
      </div>
    );
  }

  if (items.length === 0) {
    const next = track.nextSlot;
    const { label: nextLabel } = next
      ? resolveSlotLabel(next, contentMap)
      : { label: null };
    return (
      <div style={{ marginBottom: '14px' }}>
        <OffAirCard
          deviceName={track.deviceName}
          nextLabel={nextLabel}
          nextStart={next ? timeToMinutes(next.startTime) : null}
        />
      </div>
    );
  }

  const safeIndex = Math.min(index, items.length - 1);
  const activeItem = items[safeIndex];
  const activeSlotShape = { ...activeItem.__slot, contentId: activeItem.contentId, name: activeItem.name, mediaType: activeItem.mediaType };
  const { label: activeLabel } = resolveSlotLabel(activeSlotShape, contentMap);
  const activeAsset = resolveSlotAsset(activeSlotShape, contentMap);
  const accent = hashColor(activeItem.contentId, activeLabel);

  // "Ends in" = when the full campaign stops airing. For a recurring
  // multi-day intent (startDate → endDate + daily startTime/endTime), the
  // true end is endDate at endTime. For a single-day clip, end is today's
  // endTime. Scan every slot on the track for the active content; take the
  // furthest-future (endDate + endTime) across slot.contentId and
  // slot.loopItems so a loop tail + solo-extension both count correctly.
  const trackSlots = Array.isArray(track?.slots) ? track.slots : [];
  const endAtMs = (() => {
    const cid = activeItem.contentId;
    const todayMid = new Date();
    todayMid.setHours(0, 0, 0, 0);
    const timeToEpochOn = (iso, hhmm) => {
      const mins = timeToMinutes(hhmm);
      if (!iso) return todayMid.getTime() + mins * 60000;
      const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
      if (Number.isNaN(d.getTime())) return todayMid.getTime() + mins * 60000;
      return d.getTime() + mins * 60000;
    };
    let maxEpoch = timeToEpochOn(
      activeItem.__slot?.endDate || activeItem.endDate || null,
      activeItem.endTime,
    );
    for (const s of trackSlots) {
      const inTop = s.contentId && s.contentId === cid;
      const inLoop = Array.isArray(s.loopItems)
        && s.loopItems.some((li) => (li.contentId || li.content_id) === cid);
      if (!inTop && !inLoop) continue;
      const e = timeToEpochOn(s.endDate || s.date || null, s.endTime);
      if (e > maxEpoch) maxEpoch = e;
    }
    return maxEpoch;
  })();
  const endsInMs = endAtMs - nowMs;
  const endsIn = endsInMs > 0 ? formatCountdown(endsInMs) : 'ending…';

  // Build a set of contentIds already airing so we don't surface "Next up"
  // for a tail slot that's just the same content continuing.
  const airingIds = new Set(items.map((x) => x.contentId).filter(Boolean));

  let nextCopy;
  if (isLooping) {
    const upcomingItem = items[(safeIndex + 1) % items.length];
    const { label } = resolveSlotLabel(
      { ...upcomingItem.__slot, contentId: upcomingItem.contentId, name: upcomingItem.name, mediaType: upcomingItem.mediaType },
      contentMap,
    );
    nextCopy = `Then: ${label}`;
  } else if (track.nextSlot && !airingIds.has(track.nextSlot.contentId)) {
    const { label } = resolveSlotLabel(track.nextSlot, contentMap);
    nextCopy = `Next up: ${label} at ${formatMinutes(timeToMinutes(track.nextSlot.startTime))}`;
  } else {
    nextCopy = 'Nothing else scheduled today';
  }

  return (
    <div style={{ marginBottom: '14px' }}>
      <div style={cardBase}>
        <Thumbnail url={activeAsset?.thumbnailUrl} mediaType={activeAsset?.mediaType} accentColor={accent?.bg} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <LiveRibbon
            deviceName={track.deviceName}
            isLooping={isLooping}
            count={items.length}
          />
          <div style={{
            fontSize: '14px', fontWeight: 700, color: 'var(--text-strong)',
            marginTop: '2px',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {activeLabel}
          </div>
          <div style={{
            fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px',
          }}>
            ends in {endsIn}
          </div>
          <div style={{
            fontSize: '11px', color: 'var(--text-subtle)', marginTop: '4px',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {nextCopy}
          </div>
          {isLooping && (
            <Dots
              count={items.length}
              active={safeIndex}
              onPick={setIndex}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default OnAirNow;
